import ShareDB, { type Connection } from 'sharedb/lib/client';
import ClientBase, { type ObjectOpHistory, redactOp } from './ClientBase';
import { type Op, type Doc as ShareDBDoc } from 'sharedb/lib/sharedb';
import otWhiteboard from '@aha-app/ot-whiteboard';
import Serialization from './Serialization';
import Debug from 'debug';
import trackEvent from '../utils/analytics';
import { type OtWhiteboardOp } from '../ot-whiteboard';
import type WhiteboardController from '../controllers/WhiteboardController';

export interface PendingOp {
  sentAt: number;
  retries: number;
  op: Op;
}

type Doc = {
  pendingOps: PendingOp[];
  inflightOp: PendingOp | null;
  _setType: (type: string | null) => void;
  _hardRollback: () => void;
} & ShareDBDoc;

const debug = Debug('whiteboard:client');
const debugOps = Debug('whiteboard:clientOps');

const OP_BUFFER_SIZE = 100;

// ops per websocket message, to avoid overloading the backend
const OPS_MESSAGE_LIMIT = 50;

ShareDB.types.register(otWhiteboard.type);

/*
  Document format:

  {
    objects: { // All objects in document, keyed by unique ID.
      '1234': {
        type: 'rect',
        top: 10,
        left: 20,
        textContent: 'Foobar',
        fill: '#999'
      },
      '1235': {
        type: 'group',
        top: 10,
        left: 20,
        objects: ['1234'] // Unique IDs of contained objects.
      }
    }

    stack: [] // Ordered stack of visible object unique IDs. The stack is
              // obsolete and no longer used. Ordering is now implemented
              // in the `layer` and `z` properties.
  }
*/

// Time in milliseconds to delay sending property updates so that real-time
// updates don't flood the network.
const PROPERTY_UPDATE_DELAY = 800;

// Manage the communication between the whiteboard and the ShareDB server that
// handles coordination between multiple whiteboards and auto-saving.
//
// Read this blog https://www.aha.io/blog/text-editor before trying to
// understand this code.
class Client extends ClientBase {
  /** Op buffering and compaction
   * 1. New ops are added to bufferedOperations first in their raw form
   * 2. When bufferedOperations reaches a limit, or before we need to send, it is compacted and added to
   *    pendingOperations. Compaction can be expensive, so we defer it
   * 3. ops in pendingOperations are moved to sendBuffer when we are ready to send them
   * 4. ops in sendBuffer are sent in batches if needed
   */
  private bufferedOperations: OtWhiteboardOp[] = [];
  private pendingOperations: OtWhiteboardOp[] | null = null;
  private sendBuffer: OtWhiteboardOp[] = [];

  // Timer for sending pending operation updates.
  private pendingOperationsTimer: ReturnType<typeof setTimeout> | null = null;
  // Whether we have already tracked an edit.
  private _hasTrackedEdit = false;
  private sendBufferTimeout: ReturnType<typeof setTimeout> | null = null;

  doc: Doc;
  private readonly connection: Connection;
  /** Whether to discard any outgoing operations generated by this whiteboard.
  This is so that users who don't have permission to alter the whiteboard
  can run JIT data migrations or layout effects without emitting writes
  that will be rejected by Faye. **/
  discardOutgoingOps: boolean = false;

  private lastRemoteOpTimestampsByType: Record<string, number | 'now'>;

  // Types of fabric object which do not participate in collaboration.
  static ignoredObjectTypes = [
    'activeSelection',
    'line',
    'guideSpace',
    'guideAlign',
    'mindMapConnector',
  ];

  constructor(
    controller: WhiteboardController,
    connection: Connection,
    documentCollection: string,
    documentId: string
  ) {
    super(controller, false);
    this.controller.actionConnecting();

    this.connection = connection;
    this.doc = this.connection.get(documentCollection, documentId) as Doc;

    this.lastRemoteOpTimestampsByType = {};

    // ensure any pending operations are sent to the server prior to unload
    window.addEventListener('beforeunload', this.beforeUnload);
  }

  destroy() {
    // Flush any pending operations.
    this.sendPendingOperations();

    // Unsubscribe and free document (but doesn't delete on server).
    this.doc.destroy();

    window.removeEventListener('beforeunload', this.beforeUnload);
  }

  private readonly beforeUnload = () => {
    this.destroy();
  };

  // Initialize the client. We can only do this when the canvas is ready to
  // start accepting changes.
  initialize(initialVersion: number, initialData: unknown) {
    this.doc.on('op', (op, local) => this.handleOps(op, local));

    this.prepareDocument(initialVersion, initialData);

    if (this.connection.state === 'connected') {
      this.subscribe();
    } else {
      this.connection.once('connected', () => this.subscribe());
    }
  }

  handleOps(ops: OtWhiteboardOp[], local: boolean) {
    if (!ops) return;

    this.controller.dispatchEvent('whiteboard.operation');

    if (local) return; // Ignore locally generated operations.

    if (debug.enabled) {
      debug(
        `Got ${local ? 'local' : 'remote'} op at version ${this.doc.version}:`
      );
      debug(ops);
    }
    if (debugOps.enabled) this.prettyPrintOps(ops, 'remote');

    const remoteOpDatastores = new Set(ops.map(o => o.p[0]));
    remoteOpDatastores.forEach(storeName => {
      this.lastRemoteOpTimestampsByType[storeName] = 'now';
    });

    this.controller.aroundBulkAddingCode(() => {
      this.applyRemoteOperations(ops);
    });
    this.undoManager.remoteOps(ops);

    // Update last-op timestamps for each datastore that has an op in this batch.
    const processedAt = new Date().getTime();
    remoteOpDatastores.forEach(storeName => {
      this.lastRemoteOpTimestampsByType[storeName] = processedAt;
    });
  }

  getTimesSinceLastRemoteOp() {
    const now = new Date().getTime();
    const storeTimes = Object.entries(this.lastRemoteOpTimestampsByType).map(
      ([dataStoreName, lastOpProcessedAt]) => {
        if (lastOpProcessedAt === 'now') {
          return `${dataStoreName}=now`;
        }
        const timeSinceLastOp = now - lastOpProcessedAt;
        return `${dataStoreName}=${timeSinceLastOp}ms`;
      }
    );

    const message =
      storeTimes.length === 0
        ? 'No remote ops received'
        : storeTimes.join(', ');

    return `Times since last remote ops: ${message}`;
  }

  queueOp(op: OtWhiteboardOp | OtWhiteboardOp[]) {
    if (!op) return;
    if (this.discardOutgoingOps) {
      this.checkCanDiscardOp(op);
      debug(`Discarding outgoing op`, op);
      return;
    }
    if (!Array.isArray(op)) op = [op];

    super.queueOp(op);

    if (!this._hasTrackedEdit) {
      trackEvent('Feature-7094629539429452113', 'User edits whiteboard');
      this._hasTrackedEdit = true;
    }

    if (debugOps.enabled) this.prettyPrintOps(op, 'local');

    // We have a timing problem with operations that need to be transformed. If
    // we delay them here then they will be out of sync with remote operations
    // that have already been applied against the sharedb version of the
    // document. To avoid this we only delay property updates, and we send
    // any delayed updates before applying a non property update - to ensure
    // that operations stay in order.

    if (this.operationsCanBeDelayed(op)) {
      // Operation can be delayed. Incorporate the new operations into the pending set.
      this.addToPendingOperations(op);

      // Start timer to send the operations.
      if (!this.pendingOperationsTimer) {
        this.pendingOperationsTimer = setTimeout(
          () => this.sendPendingOperations(),
          PROPERTY_UPDATE_DELAY
        );
      }
    } else {
      // Combine with any pending operations and send immediately.
      this.addToPendingOperations(op);
      // bump to next tick, so that lots of single ops sent synchronously will be batched
      process.nextTick(() => {
        this.sendPendingOperations();
      });
    }
  }

  // Only property change operations can be delayed.
  operationsCanBeDelayed(ops: OtWhiteboardOp[]) {
    return ops.every(op => op.hasOwnProperty('cp'));
  }

  // actually adds to a buffer, with ops added to pending operations when they reach a limit, or before sending
  addToPendingOperations(ops: OtWhiteboardOp[]) {
    this.bufferedOperations.push(...ops);

    if (this.bufferedOperations.length > OP_BUFFER_SIZE) {
      this.compactBufferedOperations();
    }
  }

  // compacts any buffered operations, add them to the pending array, and resets the buffer
  compactBufferedOperations() {
    this.pendingOperations = otWhiteboard.type.compose(
      this.pendingOperations ?? [],
      this.bufferedOperations
    );
    this.bufferedOperations = [];
  }

  sendPendingOperations() {
    this.compactBufferedOperations();

    if (this.pendingOperations) {
      this.sendOps(this.pendingOperations);
      this.pendingOperations = null;
    }

    // Reset the timer.
    if (this.pendingOperationsTimer) {
      clearTimeout(this.pendingOperationsTimer);
      this.pendingOperationsTimer = null;
    }
  }

  abortPendingOperations() {
    this.pendingOperations = null;
    if (this.pendingOperationsTimer) {
      clearTimeout(this.pendingOperationsTimer);
      this.pendingOperationsTimer = null;
    }

    this.bufferedOperations = [];

    this.sendBuffer = [];
    if (this.sendBufferTimeout) {
      clearTimeout(this.sendBufferTimeout);
      this.sendBufferTimeout = null;
    }
  }

  removePendingOperationsFor(op: OtWhiteboardOp) {
    const filter = (pOp: OtWhiteboardOp) =>
      pOp.p[0] !== op.p[0] || pOp.p[1] !== op.p[1];

    // ensure operations are filtered in all the places they could be waiting
    if (this.bufferedOperations) {
      this.bufferedOperations = this.bufferedOperations.filter(filter);
    }

    if (this.pendingOperations) {
      this.pendingOperations = this.pendingOperations.filter(filter);
    }

    if (this.sendBuffer) {
      this.sendBuffer = this.sendBuffer.filter(filter);
    }
  }

  sendOps(ops?: OtWhiteboardOp[]) {
    if (!ops || ops.length === 0) return;

    debug('Sending ops: ');
    debug(ops);

    if (this.needsCatchup()) {
      debug('Operations backed up, not sending.');
      this.controller.actionBackedUp();
    }

    this.sendBuffer.push(...ops);
    this.bufferedSendOps();
  }

  private bufferedSendOps() {
    // don't send if we're waiting for the buffer
    if (this.sendBufferTimeout) {
      return;
    }

    const ops = this.sendBuffer.splice(0, OPS_MESSAGE_LIMIT);

    // @ts-expect-error using legacy call signature here, not represented in types
    this.doc.submitOp(ops, (err: Error) => {
      if (err) {
        console.error(
          `Error submitting ops: ${err} | ${JSON.stringify(ops.map(redactOp))}`
        );
        this.controller.fuzzer?.fail();
        this.rollback();
      } else {
        if (this.controller.fuzzer?.running()) {
          Serialization.validateDoc(this.controller, this.doc.data);
        }
        const batchId = performance.now();
        for (const op of ops) {
          if ('co' in op || 'do' in op) {
            const dataStore = op.p[0];
            const objectId = op.p[1];
            const historyEntry: ObjectOpHistory = {
              action: 'co' in op ? 'create' : 'delete',
              by: 'local',
              at: performance.now(),
              batchId,
            };
            this.appendObjectHistory(dataStore, objectId, historyEntry);
          }
        }
      }
    });

    // if there were more pending ops than we can send in one go, wait a short time then send another batch
    if (this.sendBuffer.length) {
      this.sendBufferTimeout = setTimeout(() => {
        this.sendBufferTimeout = null;
        this.bufferedSendOps();
      }, 100);
    }
  }

  /**
   * True if we have been processing changes at a reasonable rate,
   * false if changes have been backed up and there is a risk of
   * losing inflight changes.
   *
   * This is an attempt to catch errors where the client gets out of sync due
   * to a bad connection, but not one that has been clearly disconnected.
   * Imagine a connection that is spotty so you cant get your changes through,
   * but it does not trigger the disconnected state. You can simulate this bad
   * state in Chrome by turning the network to throttling to "Offline". After
   * making some changes, this state will trigger. Shortly after it will
   * probably say you are disconnected as well.
   */
  needsCatchup() {
    if (!this.doc) return true;

    // inflightOp is the current operation being sent to the server. If that
    // operation fails for some reason, it will be queued back up in pendingOps,
    // with retries and sentAt set.
    const op = this.doc.inflightOp || this.doc.pendingOps[0];

    if (!op) return false;
    if (op.retries > 1) return true;
    if (Date.now() - op.sentAt > 15000) return true;

    return false;
  }

  subscribe() {
    this.doc.subscribe(err => {
      debug('Subscribe callback');
      if (err) {
        console.error(err);
        // TODO: retry?
        return;
      }
      debug(`Got snapshot: ${this.doc.version}`);
      debug(this.doc.data);

      if (!this.doc.data) {
        // The server sent us an invalid document. This should never happen.
        console.error('Server sent null doc');
        return;
      }

      // Successfully subscribed. Allow editing of the whiteboard to start.
      this.controller.actionConnected();

      try {
        this.processingRemoteOps = true;

        Serialization.restoreDocument(this.controller, this.doc.data);
      } catch (e) {
        this.controller.actionHandleError(e);

        return;
      } finally {
        this.processingRemoteOps = false;
      }

      this.controller.documentDidLoad();
    });
  }

  prepareDocument(initialVersion: number, initialData: unknown) {
    if (initialData) {
      debug('Ingesting document');
      this.doc.ingestSnapshot(
        {
          data: initialData,
          v: initialVersion,
          type: otWhiteboard.type,
        },
        err => {
          if (err) {
            console.error(`Error ingesting snapshot: ${err}`);
          }
          // Subscribe will be called with this document and load it into
          // the canvas.
        }
      );
    }
  }

  // Something went wrong, reload document from server. This is a last resort
  // because it will discard any work the user has done that hasn't been
  // accepted by the server.
  rollback() {
    console.error('Rollback and subscribe again');
    this.controller.actionShowMessage({
      message: 'Whiteboard out of sync. Try reloading the page to continue.',
      type: 'error',
    });

    // Clear the canvas.
    this.controller.canvas.discardActiveObject();

    for (const dataStore of Object.values(this.controller.dataStores)) {
      dataStore.clear();
    }

    this.controller.undoManager.reset();
    this.controller.canvas._objects = [];
    // Abort any in-progress operations.
    this.abortPendingOperations();

    // Reset the document by doing a hard rollback.
    this.doc._hardRollback();
    this.subscribe();
  }

  // Pretty print operations.
  prettyPrintOps(ops: OtWhiteboardOp[], source) {
    for (const op of ops) {
      if ('co' in op) {
        // Create object on canvas.
        debugOps(
          `${source} - Create ${op.p[1]} ${op.co.type} ${op.co.shapeName || ''}`
        );
      } else if ('do' in op) {
        debugOps(
          `${source} - Delete ${op.p[1]} ${op.do.type} ${op.do.shapeName || ''}`
        );
      } else if (op.hasOwnProperty('cp')) {
        debugOps(`${source} - Update ${op.p.join('.')}: ${op.dp} -> ${op.cp}`);
      } else {
        debugOps(`${source} - Unhandled operation: ${JSON.stringify(op)}`);
      }
    }
  }
}

export default Client;
